import { trackById, trackByIndex } from 'src/app/shared/utils/track-by.util';
import { Component, Inject, OnInit, ChangeDetectionStrategy } from '@angular/core';

import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { DashboardWidgetDefinition, GridWidgetConfig } from 'src/app/models/dashboard/dashboard-widget.model';
import { getAllWidgetDefinitions, WIDGET_TYPE_KEYS } from '../../../components/dashboard/dashboard-widgets/dashboard-widget-registry';
import { RolePermissionObjects } from 'src/app/shared/constants/rolePermissionObjects.constants';
import { GridViewService } from 'src/app/services/grid-view.service';
import { UserViewLookup } from 'src/app/models/userView.model';
import { AuthService } from 'src/app/services/auth.service';
import { CommonService } from 'src/app/services/common.service';
import { PermissionService } from 'src/app/services/permission.service';

export interface EntityOption {
  objectId: string;
  name: string;
  icon: string;
  dependsOnPermission?: string;
}

export interface WidgetPickerResult {
  widget: DashboardWidgetDefinition;
  gridConfig?: GridWidgetConfig;
}

export const GRID_ENTITY_OPTIONS: EntityOption[] = [
  { objectId: RolePermissionObjects.Sample, name: 'Samples', icon: 'science' },
  { objectId: RolePermissionObjects.Agent, name: 'Agents', icon: 'coronavirus' },
  { objectId: RolePermissionObjects.AgentGroup, name: 'Agent Groups', icon: 'workspaces' },
  { objectId: RolePermissionObjects.Control, name: 'Controls', icon: 'verified_user' },
  { objectId: RolePermissionObjects.LabRequisition, name: 'Lab Requisitions', icon: 'assignment', dependsOnPermission: RolePermissionObjects.Sample },
  { objectId: RolePermissionObjects.Survey, name: 'Surveys', icon: 'poll', dependsOnPermission: RolePermissionObjects.Sample },
  { objectId: RolePermissionObjects.SEG, name: 'SEGs', icon: 'groups' },
  { objectId: RolePermissionObjects.CalibrationEquipment, name: 'Calibration Equipment', icon: 'precision_manufacturing' },
  { objectId: RolePermissionObjects.SamplingEquipment, name: 'Sampling Equipment', icon: 'build' },
  { objectId: RolePermissionObjects.Ppe, name: 'PPE', icon: 'health_and_safety' },
  { objectId: RolePermissionObjects.User, name: 'Users', icon: 'people' },
  { objectId: RolePermissionObjects.Report, name: 'Reports', icon: 'description' },
  { objectId: RolePermissionObjects.Jurisdiction, name: 'Jurisdictions', icon: 'gavel' },
  { objectId: RolePermissionObjects.FileAttachment, name: 'File Attachments', icon: 'attach_file' },
];

@Component({
    standalone: true,
    selector: 'app-widget-picker-dialog',
    imports: [MatDialogModule, TranslateModule],
    templateUrl: './widget-picker-dialog.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./widget-picker-dialog.component.scss']
})
export class WidgetPickerDialogComponent implements OnInit {
  availableWidgets: DashboardWidgetDefinition[] = [];
  entityOptions: EntityOption[] = [];
  availableViews: UserViewLookup[] = [];

  currentStep = 1;
  selectedWidget?: DashboardWidgetDefinition;
  selectedEntity?: EntityOption;
  selectedView?: UserViewLookup;

  isLoadingViews = false;

  constructor(
    public dialogRef: MatDialogRef<WidgetPickerDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: unknown,
    private gridViewService: GridViewService,
    private authService: AuthService,
    private commonService: CommonService,
    private permissionService: PermissionService
  ) { }

  ngOnInit(): void {
    this.availableWidgets = getAllWidgetDefinitions().filter(
      w => !w.requiredPermission || this.permissionService.hasAllReadPermissions(w.requiredPermission)
    );
    this.entityOptions = GRID_ENTITY_OPTIONS.filter(
      e => this.permissionService.hasReadPermission(e.objectId)
        && (!e.dependsOnPermission || this.permissionService.hasReadPermission(e.dependsOnPermission))
    );
  }

  get isGridWidget(): boolean {
    return this.selectedWidget?.type === WIDGET_TYPE_KEYS.GRID_VIEW;
  }

  get totalSteps(): number {
    return this.isGridWidget ? 3 : 1;
  }

  get canProceed(): boolean {
    switch (this.currentStep) {
      case 1: return !!this.selectedWidget;
      case 2: return !!this.selectedEntity;
      case 3: return true; // View selection is optional (will use default)
      default: return false;
    }
  }

  get stepTitle(): string {
    switch (this.currentStep) {
      case 1: return 'Choose a Widget';
      case 2: return 'Select Entity Type';
      case 3: return 'Select View';
      default: return '';
    }
  }

  selectWidget(widget: DashboardWidgetDefinition): void {
    this.selectedWidget = widget;

    // For non-grid widgets, close immediately
    if (!this.isGridWidget) {
      this.confirm();
    } else {
      // For grid widgets, proceed to entity selection
      this.currentStep = 2;
    }
  }

  selectEntity(entity: EntityOption): void {
    this.selectedEntity = entity;
    this.loadViewsForEntity();
    this.currentStep = 3;
  }

  selectView(view: UserViewLookup | null): void {
    this.selectedView = view;
  }

  private loadViewsForEntity(): void {
    if (!this.selectedEntity) return;

    this.isLoadingViews = true;
    this.availableViews = [];

    const currentUser = this.authService.getCurrentUser();
    const currentOrg = this.commonService.selectedOrgEmitter.value;

    const filterBy = JSON.stringify({
      objectId: this.selectedEntity.objectId,
      createdByUserId: currentUser.userId,
      organizationLevelId: currentOrg?.id
    });

    this.gridViewService.getAll(filterBy).subscribe({
      next: (views) => {
        this.availableViews = views || [];
        this.isLoadingViews = false;
      },
      error: () => {
        this.availableViews = [];
        this.isLoadingViews = false;
      }
    });
  }

  goBack(): void {
    if (this.currentStep <= 1) return;

    this.currentStep--;
    if (this.currentStep === 1) {
      this.selectedWidget = undefined;
    }
    if (this.currentStep <= 2) {
      this.selectedEntity = undefined;
      this.selectedView = undefined;
    }
  }

  confirm(): void {
    if (!this.selectedWidget) return;

    const result: WidgetPickerResult = { widget: this.selectedWidget };

    if (this.isGridWidget && this.selectedEntity) {
      const selectedViewId = this.selectedView?.id;
      result.gridConfig = {
        objectId: this.selectedEntity.objectId,
        gridViewId: typeof selectedViewId === 'string' ? selectedViewId : undefined,
        viewName: this.selectedView?.name
      };
    }

    this.dialogRef.close(result);
  }

  close(): void {
    this.dialogRef.close();
  }

  public trackByIndex = trackByIndex;
  public trackById = trackById;
}
